import React, { useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { keyBy } from 'lodash';
import PropTypes from 'prop-types';
import { useModulePage } from '../../hooks/queryParams/useModulePage';
import { MultiLayerDrawer } from '../../modules/multi-layer-drawer/MultiLayerDrawer';
import { ModuleComponent } from '../ModuleComponent/ModuleComponent';
import useAvailableRoutes from './hooks/useAvailableRoutes';

export const HOME_PATH = '/';
const idToPath = ({ id, basePath = '' }, keepQueryParams) =>
    `${basePath}/${id}${keepQueryParams ? window.location.search : ''}`;

const renderRoute = (route, defaultRoute, isAvailable, keepQueryParams) => {
    const { id, component: Component, componentProps = {} } = route;

    return (
        <Route
            key={id}
            render={props =>
                isAvailable ? (
                    <Component {...props} {...componentProps} />
                ) : (
                    <Redirect from={HOME_PATH} to={idToPath(defaultRoute, keepQueryParams)} />
                )
            }
            path={idToPath(route)}
        />
    );
};

export const RouteList = ({ routes, keepQueryParams }) => {
    const routesArr = useMemo(() => Object.values(routes), [routes]);
    const availableRoutes = useAvailableRoutes(routes);

    const { route = {}, path: modulePagePath } = useModulePage(routes);

    const defaultRoute = useMemo(
        () =>
            availableRoutes.find(elm => elm.isDefault) ||
            (availableRoutes[0] && !availableRoutes[0].nonAuthAccess ? availableRoutes[0] : undefined),
        [availableRoutes]
    );

    const routesToRender = useMemo(() => {
        let routes = availableRoutes.filter(route => !route.legacy);

        const availableRoutesMap = keyBy(routes, 'id');

        return routesArr.map(route => renderRoute(route, defaultRoute, availableRoutesMap[route.id], keepQueryParams));
    }, [availableRoutes, routesArr, defaultRoute, keepQueryParams]);

    const path = modulePagePath ?? idToPath(route);

    if (!defaultRoute && !availableRoutes.length) {
        return null;
    }

    return (
        <>
            <Route path={path} render={params => <ModuleComponent path={path} routes={routes} {...params} />} />
            <Switch>
                {routesToRender}
                {defaultRoute && <Redirect from={HOME_PATH} to={idToPath(defaultRoute, keepQueryParams)} />}
            </Switch>
            <MultiLayerDrawer />
        </>
    );
};

RouteList.propTypes = {
    /** Map of route configurations */
    routes: PropTypes.object,
};

RouteList.defaultProps = {
    setFatalError: () => {},
};
